import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Form, Input, Spin } from "antd";
import { LockOutlined } from "@ant-design/icons";
import AuthShell, { primaryButtonStyle } from "./AuthShell";
import { useResetPassword } from "../viewmodels/usePasswordReset";
import { MIN_PASSWORD_LENGTH, PASSWORD_RESET_ENABLED, takeTokenFromLocation } from "../models/passwordResetModel";

// Public page opened from the reset email: /reset-password?token=…
export default function ResetPasswordPage() {
  // Read once, then the token is removed from the address bar.
  const [token] = useState(() => takeTokenFromLocation());
  const { stage, error, submit, retry } = useResetPassword(token);
  const navigate = useNavigate();
  const toSignIn = () => navigate("/", { replace: true });
  const signInButton = label => <Button type="primary" size="large" block className="login-signin-btn" style={primaryButtonStyle} onClick={toSignIn}>{label}</Button>;

  let body;
  if (stage === "checking") {
    body = <div style={{ textAlign: "center", padding: "12px 0" }}><Spin /><p className="login-text">Checking your link…</p></div>;
  } else if (stage === "unavailable") {
    body = <>
      <Alert type="error" showIcon message={error} style={{ marginBottom: 18, borderRadius: 10 }} />
      <Button size="large" block onClick={retry} style={{ borderRadius: 10, marginBottom: 12 }}>Try again</Button>
      {signInButton("Back to sign in")}
    </>;
  } else if (stage === "invalid") {
    body = <>
      <Alert type="warning" showIcon style={{ marginBottom: 14, borderRadius: 10 }} message="This link has expired" description="Reset links work once and expire after 30 minutes." />
      <p className="login-text">{PASSWORD_RESET_ENABLED ? 'Go back to sign in and choose "Forgot password?" to get a new link.' : "Ask an administrator for a new link or a password reset."}</p>
      {signInButton("Back to sign in")}
    </>;
  } else if (stage === "done") {
    body = <>
      <Alert type="success" showIcon style={{ marginBottom: 14, borderRadius: 10 }} message="Password changed" description="You've been signed out everywhere. Sign in with your new password." />
      {signInButton("Sign in")}
    </>;
  } else {
    body = <>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 18, borderRadius: 10 }} />}
      <Form layout="vertical" requiredMark={false} className="login-card-input" onFinish={({ password, confirm }) => submit(password, confirm)}>
        <Form.Item name="password" rules={[{ required: true, message: "Enter a new password" }, { min: MIN_PASSWORD_LENGTH, message: `Use at least ${MIN_PASSWORD_LENGTH} characters` }]} style={{ marginBottom: 16 }}>
          <Input.Password size="large" prefix={<LockOutlined />} placeholder="New password" autoComplete="new-password" autoFocus aria-label="New password" />
        </Form.Item>
        <Form.Item name="confirm" dependencies={["password"]} rules={[{ required: true, message: "Repeat the new password" },
          ({ getFieldValue }) => ({ validator: (_, value) => !value || value === getFieldValue("password") ? Promise.resolve() : Promise.reject(new Error("The passwords do not match")) })]} style={{ marginBottom: 22 }}>
          <Input.Password size="large" prefix={<LockOutlined />} placeholder="Repeat new password" autoComplete="new-password" aria-label="Repeat new password" />
        </Form.Item>
        <Button type="primary" htmlType="submit" size="large" block loading={stage === "saving"} className="login-signin-btn" style={primaryButtonStyle}>
          Set new password
        </Button>
      </Form>
    </>;
  }
  return <AuthShell subtitle="Set a new password">{body}</AuthShell>;
}
